import React, { useState, useEffect } from 'react';
import StudentHeader from '../../Components/StudentHeader';
import Footer from '../../Components/Footer';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';

const ResellerOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showShipModal, setShowShipModal] = useState(false);
  const [shippingData, setShippingData] = useState({
    trackingNumber: '',
    shippingProvider: '',
    estimatedDeliveryDate: ''
  });

  useEffect(() => {
    fetchOrders();
  }, []);

  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryOtp, setDeliveryOtp] = useState('');

  const handleDeliverySubmit = async (orderId) => {
    try {
      await HttpInterceptor.post(`/api/orders/${orderId}/delivered`, { otp: deliveryOtp });
      toast.success('Order marked as delivered successfully');
      setShowDeliveryModal(false);
      setSelectedOrder(null);
      setDeliveryOtp('');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to verify OTP');
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await HttpInterceptor.get('/api/resellers/orders');
      setOrders(response.data.data || []);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await HttpInterceptor.put(`/api/orders/${orderId}/status`, { orderStatus: newStatus });
      toast.success(`Order status updated to ${newStatus}`);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleShip = (order) => {
    setSelectedOrder(order);
    setShowShipModal(true);
  };

  const handleShipSubmit = async (e) => {
    e.preventDefault();
    try {
      await HttpInterceptor.post(`/api/orders/${selectedOrder._id}/ship`, shippingData);
      toast.success('Order marked as shipped');
      setShowShipModal(false);
      setSelectedOrder(null);
      setShippingData({ trackingNumber: '', shippingProvider: '', estimatedDeliveryDate: '' });
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update shipping');
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-gray-100">
      <StudentHeader />

      <main className="flex-1 container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6">My Orders</h1>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-4">Order ID</th>
                  <th className="text-left p-4">Customer</th>
                  <th className="text-left p-4">Items</th>
                  <th className="text-left p-4">Amount</th>
                  <th className="text-left p-4">Status</th>
                  <th className="text-left p-4">Date</th>
                  <th className="text-left p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <tr key={order._id} className="border-b">
                    <td className="p-4">#{order._id.slice(-8)}</td>
                    <td className="p-4">{order.userId?.name || 'N/A'}</td>
                    <td className="p-4">{order.items?.length || 0} items</td>
                    <td className="p-4">₹{order.finalAmount?.toFixed(2)}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-sm ${order.orderStatus === 'delivered' ? 'bg-green-100 text-green-700' :
                        order.orderStatus === 'shipped' ? 'bg-blue-100 text-blue-700' :
                          order.orderStatus === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                        }`}>
                        {order.orderStatus}
                      </span>
                    </td>
                    <td className="p-4">{new Date(order.createdAt).toLocaleDateString()}</td>
                    <td className="p-4">
                      {order.orderStatus !== 'cancelled' && order.orderStatus !== 'delivered' && order.orderStatus !== 'shipped' && (
                        <div className="flex items-center gap-2 mb-2">
                          <select
                            value={order.orderStatus}
                            onChange={(e) => handleStatusUpdate(order._id, e.target.value)}
                            className="text-sm border rounded px-2 py-1"
                          >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="processing">Processing</option>
                          </select>
                        </div>
                      )}

                      {order.orderStatus === 'confirmed' || order.orderStatus === 'processing' ? (
                        <button
                          onClick={() => handleShip(order)}
                          className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 w-full"
                        >
                          Mark Shipped
                        </button>
                      ) : order.orderStatus === 'shipped' ? (
                        <div className="space-y-2">
                          {order.trackingNumber && (
                            <div className="text-sm text-gray-600">
                              <p>Tracking: {order.trackingNumber}</p>
                            </div>
                          )}
                          <button
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowDeliveryModal(true);
                            }}
                            className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 w-full"
                          >
                            Complete Delivery
                          </button>
                        </div>
                      ) : order.trackingNumber ? (
                        <div className="text-sm text-gray-600">
                          <p>Tracking: {order.trackingNumber}</p>
                          <p className="text-xs">{order.shippingProvider}</p>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showShipModal && selectedOrder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <h2 className="text-xl font-bold mb-4">Mark Order as Shipped</h2>
              <form onSubmit={handleShipSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Tracking Number</label>
                  <input
                    type="text"
                    value={shippingData.trackingNumber}
                    onChange={(e) => setShippingData({ ...shippingData, trackingNumber: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Shipping Provider</label>
                  <input
                    type="text"
                    value={shippingData.shippingProvider}
                    onChange={(e) => setShippingData({ ...shippingData, shippingProvider: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Estimated Delivery Date</label>
                  <div className="bg-yellow-50 p-2 rounded text-xs text-yellow-800 mb-2">
                    Note: If left blank, a default of 5 days will be applied.
                  </div>
                  <input
                    type="date"
                    value={shippingData.estimatedDeliveryDate}
                    onChange={(e) => setShippingData({ ...shippingData, estimatedDeliveryDate: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                  />
                </div>
                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Mark Shipped
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowShipModal(false);
                      setSelectedOrder(null);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showDeliveryModal && selectedOrder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <h2 className="text-xl font-bold mb-4">Confirm Delivery</h2>
              <p className="text-gray-600 mb-4 text-sm">Ask the customer for the 6-digit OTP sent to them upon shipping.</p>
              <form onSubmit={(e) => {
                e.preventDefault();
                handleDeliverySubmit(selectedOrder._id);
              }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Enter Delivery OTP</label>
                  <input
                    type="text"
                    maxLength="6"
                    value={deliveryOtp}
                    onChange={(e) => setDeliveryOtp(e.target.value)}
                    placeholder="XXXXXX"
                    className="w-full px-4 py-2 border rounded-lg text-center text-2xl font-mono tracking-widest uppercase"
                    required
                  />
                </div>
                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Verify & Complete
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeliveryModal(false);
                      setSelectedOrder(null);
                      setDeliveryOtp('');
                    }}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </main>

      <Footer />
    </div>
  );
};

export default ResellerOrders;



